import { Component, Input, OnInit } from '@angular/core';
import { NbDialogRef } from '@nebular/theme';
import {
  CourseDifficultyItem,
  CourseStructureService,
} from '../../../services/course-structure.service';

/**
 * Content-difficulty report (Workstream G / T16c): per-objective fail-rate for
 * one course, hardest first — so dozenten/authors see which chapters and
 * objectives learners stumble on. Fed by the guided-mode judge node via
 * content_difficulty_signals. Keyed by the Moodle canonical course uuid.
 */
@Component({
  selector: 'app-course-difficulty-dialog',
  templateUrl: './course-difficulty-dialog.component.html',
  styleUrl: './course-difficulty-dialog.component.scss',
})
export class CourseDifficultyDialogComponent implements OnInit {
  @Input() course!: { id: string; fullname?: string; shortname?: string };

  loading = true;
  error = false;
  items: CourseDifficultyItem[] = [];
  chartOption: any = {};

  constructor(
    public ref: NbDialogRef<CourseDifficultyDialogComponent>,
    private svc: CourseStructureService,
  ) {}

  ngOnInit(): void {
    this.svc.getDifficultyByCanonical(this.course.id).subscribe({
      next: (d) => {
        this.items = d?.items || [];
        this.loading = false;
        this.buildChart();
      },
      error: () => {
        this.error = true;
        this.loading = false;
      },
    });
  }

  failPct(i: CourseDifficultyItem): number {
    return Math.round((i.fail_rate || 0) * 100);
  }

  private label(i: CourseDifficultyItem): string {
    const obj = (i.objective || i.objective_id || '').toString();
    const ch = i.chapter_title ? `${i.chapter_title}: ` : '';
    const s = `${ch}${obj}`;
    return s.length > 60 ? s.slice(0, 57) + '…' : s;
  }

  private colorFor(fail: number): string {
    if (fail >= 0.6) return '#ED4E1B';
    if (fail >= 0.3) return '#FFC864';
    return '#00A35C';
  }

  private buildChart(): void {
    // Horizontal bars, hardest at the top → reverse so echarts (bottom-up)
    // shows the worst first.
    const top = this.items.slice(0, 20).slice().reverse();
    this.chartOption = {
      grid: { left: 8, right: 48, top: 8, bottom: 8, containLabel: true },
      xAxis: {
        type: 'value',
        max: 100,
        axisLabel: { formatter: '{value}%' },
      },
      yAxis: {
        type: 'category',
        data: top.map((i) => this.label(i)),
        axisLabel: { width: 240, overflow: 'truncate', fontSize: 11 },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (p: any) =>
          `${p[0].name}<br/>Fehlerquote: <b>${p[0].value}%</b>`,
      },
      series: [
        {
          type: 'bar',
          barMaxWidth: 18,
          data: top.map((i) => ({
            value: this.failPct(i),
            itemStyle: { color: this.colorFor(i.fail_rate || 0), borderRadius: [0, 3, 3, 0] },
          })),
          label: { show: true, position: 'right', formatter: '{c}%', fontSize: 11 },
        },
      ],
    };
  }

  close(): void {
    this.ref.close();
  }
}
